// The node first-run gate: an intranet node (erp-core ERP_MODE=node) must be
// LICENSED and must HAVE AN OWNER before anyone can work on it, and both
// happen before sign-in, so the gate sits above /auth/login:
//   - unlicensed → the public activation page (/activate), signed in or not;
//     decided from erp-core, not from a flag (an unlicensed node's flags are
//     {} — `erp.local-mode` included):
//       GET /v1/erp/license/status `licensed:false`, probed whenever the app
//       knows it runs on a node (build mode node, or the server's /me said
//       so); else the cached /me's `node.licensed:false` (a session that was
//       open when the licence lapsed);
//   - licensed but no local user yet (GET /v1/erp/auth/status
//     `bootstrap_required:true`) → the public first-run page (/setup): the
//     owner is created with the one-time code from the erp-core log.
// Both pages stay reachable while the other step is pending (erp-core
// accepts either order: activating first creates the owner on the licence's
// business; bootstrapping first gives the business id the licence must name).
// Cloud never probes (the routes do not exist there; no 404 noise). A probe
// that fails lets the page through — the server still refuses every
// non-exempt route with 403 `unlicensed`.
//
// Wiring: buildRoutes() (src/app/router.tsx) returns withNodeLicenceGate(routes):
// /activate + /setup beside the app's routes, the rest wrapped in this gate.

import type { ReactNode } from 'react';
import { BrandMark } from '@/components/BrandMark';
import type { RouteObject } from 'react-router-dom';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthenticated } from '@/app/RequireAuth';
import { getRuntimeMode } from '@/lib/auth';
import { fetchMe, ME_QUERY_KEY, type Me } from '@/lib/me';
import { useLicenceStatus, useNodeAuthStatus } from './data/api';
import { ActivationScreen } from './screens/Activation';
import { FirstRunScreen } from './screens/FirstRun';

export const ACTIVATE_PATH = '/activate';
export const SETUP_PATH = '/setup';

/** Observes the cached /me without ever fetching it (ErpProvider owns that read). */
function useCachedMe(): Me | undefined {
  return useQuery({ queryKey: ME_QUERY_KEY, queryFn: fetchMe, enabled: false }).data;
}

function useOnNode(): { onNode: boolean; me: Me | undefined } {
  const me = useCachedMe();
  return { onNode: getRuntimeMode() === 'node' || me?.mode === 'node', me };
}

export function useNodeUnlicensed(): { unlicensed: boolean; checking: boolean } {
  const { onNode, me } = useOnNode();
  const status = useLicenceStatus(onNode);
  // The live licence status wins over a /me read from before an activation.
  const unlicensed = status.data ? status.data.licensed === false : me?.node?.licensed === false;
  return { unlicensed, checking: onNode && status.isLoading && !me };
}

/**
 * A node with no local user yet (the owner bootstrap is pending). False on
 * cloud, for a session (a session proves a user exists) and when the probe
 * fails. The status is read on every node page — signed in too — so a sign-out
 * (lock screen «انتهت الجلسة») finds it cached and never flashes the loader
 * over the tree that is navigating to /auth/login with its notice.
 */
export function useNodeBootstrapRequired(): { bootstrapRequired: boolean; checking: boolean } {
  const { onNode } = useOnNode();
  const authed = useAuthenticated();
  const status = useNodeAuthStatus(onNode);
  const signedOut = onNode && !authed;
  return { bootstrapRequired: signedOut && status.data?.bootstrap_required === true, checking: signedOut && status.isLoading };
}

export function NodeLicenceGate({ children }: { children?: ReactNode }) {
  const location = useLocation();
  const licence = useNodeUnlicensed();
  const firstRun = useNodeBootstrapRequired();
  if (licence.checking || firstRun.checking) {
    return (
      <div className="e-center" data-testid="licence-checking">
        <BrandMark mode="loading" size={72} />
        <span className="sr-only">جارٍ التحميل</span>
      </div>
    );
  }
  const path = location.pathname;
  if (licence.unlicensed && path !== ACTIVATE_PATH && path !== SETUP_PATH) return <Navigate to={ACTIVATE_PATH} replace />;
  if (!licence.unlicensed && firstRun.bootstrapRequired && path !== SETUP_PATH && path !== ACTIVATE_PATH) {
    return <Navigate to={SETUP_PATH} replace />;
  }
  return <>{children ?? <Outlet />}</>;
}

/** The public routes the system module adds next to /auth/login. */
export const systemPublicRoutes: RouteObject[] = [
  { path: ACTIVATE_PATH, element: <ActivationScreen variant="public" /> },
  { path: SETUP_PATH, element: <FirstRunScreen /> },
];

/** [/activate, /setup, { element: <NodeLicenceGate/>, children: routes }] — one call in buildRoutes(). */
export function withNodeLicenceGate(routes: RouteObject[]): RouteObject[] {
  return [...systemPublicRoutes, { element: <NodeLicenceGate />, children: routes }];
}
